import { Injectable } from '@angular/core';
import { createClient, Entry, EntryCollection, EntrySkeletonType } from 'contentful';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AxiosAngularAdapterService } from './axios-angular-adapter.service';
import { TypePage, TypePageFields } from '../../../models/contentful-content-types/page';
import { TypeHeaderFields } from '../../../models/contentful-content-types/header.model';
import { Footer } from '../../components/footer/models/footer.model';
import { environment } from '../../../../environments/environment';

/**
 * Einziger Contentful-Client der App (Content Delivery API, read-only).
 *
 * Zwei Abfrage-Strategien für Seiten, je nach Konsument:
 * - `getPageBySlug`: generische Routen über DynamicPageComponent (Slug = URL-Pfad)
 * - `getPageByInternalTitle`: fest verdrahtete Feature-Seiten mit eigenem Component
 *   (z.B. /offers/., das nicht über DynamicPageComponent läuft)
 *
 * Seiten-Metadaten (SEO Title/Description) sind bewusst NICHT Teil dieses Service —
 * das Setzen der Metadaten ist Aufgabe der aufrufenden Komponente (Single Responsibility),
 * nicht ein versteckter Seiteneffekt im Datenzugriff.
 */
@Injectable({
  providedIn: 'root'
})
export class ContentfulService {
  private readonly cdaClient = createClient({
    space: environment.CONTENTFUL_SPACE,
    accessToken: environment.CONTENTFUL_ACCESS_TOKEN,
    environment: environment.CONTENTFUL_ENVIRONMENT,
    adapter: this.axiosAdapter.adapter
  });

  constructor(private readonly axiosAdapter: AxiosAngularAdapterService) {}

  /** Seite anhand ihres URL-Slugs (z.B. "/home/") — für die generische DynamicPageComponent-Route. */
  getPageBySlug(slug: string, locale: string): Observable<TypePageFields> {
    const request = this.cdaClient.getEntries({
      content_type: 'page',
      locale,
      include: 10,
      'fields.slug': this.normalizeSlug(slug)
    } as never) as unknown as Promise<{ items: Array<{ fields: TypePageFields }> }>;
    return from(request).pipe(map((response) => response.items[0]?.fields ?? ({} as TypePageFields)));
  }

  /**
   * Contentful speichert Slugs stets mit abschließendem Slash (z. B. "/karriere/").
   * document.location.pathname liefert den Pfad beim SSR-Prerendering ohne diesen
   * Slash, wodurch die Slug-Abfrage sonst leer bleibt.
   */
  private normalizeSlug(slug: string): string {
    if (!slug) return '/';
    return slug.endsWith('/') ? slug : `${slug}/`;
  }

  /** Seite anhand ihres internen Titels — für Seiten mit eigenem Component statt DynamicPageComponent. */
  getPageByInternalTitle(internalTitle: string, locale: string): Observable<TypePageFields | null> {
    const request = this.cdaClient.getEntries({
      content_type: 'page',
      locale,
      include: 10,
      'fields.internalTitle': internalTitle
    } as never) as unknown as Promise<{ items: Array<{ fields: TypePageFields }> }>;
    return from(request).pipe(map((response) => response.items[0]?.fields ?? null));
  }

  /**
   * Header und Footer der Website — es existiert jeweils genau ein Entry, das alle
   * Seiten teilen.
   *
   * Seiten über DynamicPageComponent bekommen ihre Chrome als Sections der jeweiligen
   * Page mitgeliefert. Komponenten mit eigener Route (z.B. /widerruf/) haben keine
   * Page-Entry und laden Header/Footer deshalb direkt über den Content-Type, statt sich
   * an eine thematisch fremde Seite zu hängen.
   */
  getSiteChrome(locale: string): Observable<{ header: TypeHeaderFields | null; footer: Footer | null }> {
    const load = <T>(contentType: string) =>
      this.cdaClient.getEntries({
        content_type: contentType,
        locale,
        include: 10,
        limit: 1
      } as never) as unknown as Promise<{ items: Array<{ fields: T }> }>;

    return from(Promise.all([load<TypeHeaderFields>('header'), load<Footer>('footer')])).pipe(
      map(([headerResponse, footerResponse]) => ({
        header: headerResponse.items[0]?.fields ?? null,
        footer: footerResponse.items[0]?.fields ?? null
      }))
    );
  }

  /** Rohe Content-Type-Entries (inkl. sys/Metadaten) für Listing-Abfragen, z.B. Team-Kacheln. */
  getContentTypeEntries<T extends EntrySkeletonType>(options: object, query?: object): Observable<Entry<T>[]> {
    return from(this.cdaClient.getEntries<T>(Object.assign(options, query) as never)).pipe(
      map((response: EntryCollection<T>) => response.items)
    );
  }

  /** Alle Seiten-Entries fürs Sitemap-/Routing-Bootstrap (siehe app.config.ts). */
  getPages(): Observable<Entry<TypePage>[]> {
    return this.getContentTypeEntries<TypePage>({ content_type: 'page', locale: 'de-DE' });
  }
}
